/**
 * XP, levels, streaks and achievements.
 *
 * These exist to make practice habitual, and that is all. The design document
 * is explicit that XP must not become the measure of mathematical ability
 * (§10) — so nothing in the platform *unlocks* on XP, recommendations never
 * consider it, and mastery is always the number shown first.
 */

import type { LearnerProfile, Streak } from './types.ts';
import { computeMastery, type ConceptState } from './mastery.ts';

/* -------------------------------------------------------------------------- */
/* XP                                                                         */
/* -------------------------------------------------------------------------- */

export type XpSignal = {
  correct: boolean;
  difficulty: number;
  /** Right on the first attempt at this exercise. */
  firstTry: boolean;
  /** The attempt came from the review queue rather than fresh practice. */
  isReview?: boolean;
  usedHint?: boolean;
  /**
   * Consecutive correct answers *before* this one. Raises the reward for a
   * sustained run without ever lowering it: a broken combo costs the next
   * answer its bonus, never the student's existing XP.
   */
  combo?: number;
};

export const XP_LESSON_COMPLETE = 20;

/** Consecutive correct answers at which the combo bonus stops growing. */
export const COMBO_CAP = 10;

/** The most a combo can multiply an answer's XP by, at `COMBO_CAP`. */
export const COMBO_MAX_MULTIPLIER = 1.5;

/**
 * The XP multiplier for a run of `combo` correct answers.
 *
 * Grows to 1.5× over ten answers and then stops. A cap matters: an uncapped
 * multiplier rewards the longest sitting rather than the best thinking, and
 * this platform's whole position is that twelve answers in one evening are
 * worth less than four answers on three days (see the review ladder).
 */
export function comboMultiplier(combo: number): number {
  const run = Math.max(0, Math.min(COMBO_CAP, Math.floor(combo)));
  return 1 + ((COMBO_MAX_MULTIPLIER - 1) * run) / COMBO_CAP;
}

/**
 * A wrong answer still earns a little: the student showed up and tried, and
 * penalising attempts teaches guess-avoidance rather than mathematics.
 */
export function xpForAttempt(signal: XpSignal): number {
  if (!signal.correct) return 1;
  let xp = 4 + 2 * (signal.difficulty - 1);
  if (signal.firstTry) xp += 2;
  if (signal.usedHint) xp = Math.round(xp * 0.6);
  if (signal.isReview) xp = Math.round(xp * 1.25);
  if (signal.combo) xp = Math.round(xp * comboMultiplier(signal.combo));
  return Math.max(1, xp);
}

/**
 * Level thresholds grow quadratically: level n starts at 25·n·(n−1) XP, so
 * early levels arrive quickly and later ones represent real work.
 */
export function xpForLevel(level: number): number {
  return 25 * level * (level - 1);
}

export type LevelProgress = {
  level: number;
  title: string;
  xpIntoLevel: number;
  xpForNextLevel: number;
  fraction: number;
};

const LEVEL_TITLES = [
  'Beginner',
  'Explorer',
  'Apprentice',
  'Problem Solver',
  'Thinker',
  'Analyst',
  'Strategist',
  'Mathematician',
];

export function levelForXp(xp: number): LevelProgress {
  let level = 1;
  while (xp >= xpForLevel(level + 1)) level += 1;
  const base = xpForLevel(level);
  const next = xpForLevel(level + 1);
  const span = next - base;
  return {
    level,
    title: LEVEL_TITLES[Math.min(LEVEL_TITLES.length - 1, Math.floor((level - 1) / 3))] as string,
    xpIntoLevel: xp - base,
    xpForNextLevel: span,
    fraction: span === 0 ? 0 : (xp - base) / span,
  };
}

/* -------------------------------------------------------------------------- */
/* Streaks                                                                    */
/* -------------------------------------------------------------------------- */

/** Local calendar day. Streaks must follow the student's midnight, not UTC's. */
export function dayKey(now: number): string {
  const date = new Date(now);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function daysBetween(from: string, to: string): number {
  const parse = (key: string): number => {
    const [y, m, d] = key.split('-').map(Number) as [number, number, number];
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((parse(to) - parse(from)) / 86_400_000);
}

/**
 * Rest days.
 *
 * A streak that dies the first time a child is ill, travelling, or simply had
 * a bad week punishes the wrong thing: the point of the streak is to reward
 * coming back, and the moment it resets to zero the cheapest move is to stop
 * caring. So the platform banks a rest day for every five days practised, and
 * spends one automatically to cover a single missed day.
 *
 * Two at most, and one missed day at most — a mechanic that can paper over a
 * fortnight is no longer measuring a habit. Rest days are earned by practice
 * and cannot be bought; nothing about them is behind the paywall.
 */
export const STREAK_FREEZE_EVERY = 5;
export const MAX_STREAK_FREEZES = 2;

/** Rest days held right now, tolerating profiles written before they existed. */
export function freezesHeld(streak: Streak): number {
  return Math.max(0, Math.min(MAX_STREAK_FREEZES, streak.freezes ?? 0));
}

/** Idempotent: practising twice in one day does not advance the streak twice. */
export function updateStreak(streak: Streak, now: number): Streak {
  const today = dayKey(now);
  if (streak.lastActiveDay === today) return streak;

  const gap = streak.lastActiveDay === null ? Infinity : daysBetween(streak.lastActiveDay, today);
  const held = freezesHeld(streak);

  // Exactly one missed day, and a rest day in hand: the run continues and the
  // missed day is recorded as covered rather than practised, so the day count
  // stays honest even though the streak survives.
  const rescued = gap === 2 && held > 0;
  const current = gap === 1 || rescued ? streak.current + 1 : 1;
  const remaining = rescued ? held - 1 : held;

  // One rest day banked per five days of practice, never more than two held.
  const earned = current > 0 && current % STREAK_FREEZE_EVERY === 0 ? 1 : 0;

  return {
    current,
    longest: Math.max(streak.longest, current),
    lastActiveDay: today,
    totalActiveDays: streak.totalActiveDays + 1,
    freezes: Math.min(MAX_STREAK_FREEZES, remaining + earned),
    freezeUsedOn: rescued ? previousDay(today) : (streak.freezeUsedOn ?? null),
  };
}

function previousDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d) - 86_400_000);
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

export type StreakStatus = 'none' | 'safe' | 'at-risk' | 'rescuable' | 'broken';

/**
 * Where the streak stands, in the words the UI needs.
 *
 * `at-risk` is today with nothing done yet; `rescuable` is a day already missed
 * that a banked rest day will cover the moment the student returns. Told apart
 * because the honest message differs: one is "keep it alive", the other is
 * "you have not lost it".
 */
export function streakStatus(streak: Streak, now: number): StreakStatus {
  if (streak.lastActiveDay === null) return 'none';
  const gap = daysBetween(streak.lastActiveDay, dayKey(now));
  if (gap <= 0) return 'safe';
  if (gap === 1) return 'at-risk';
  if (gap === 2 && freezesHeld(streak) > 0) return 'rescuable';
  return 'broken';
}

/**
 * Whether the streak can still be continued by practising now.
 *
 * A banked rest day counts: the run is not over while the platform is still
 * holding cover for the day that was missed.
 */
export function streakIsAlive(streak: Streak, now: number): boolean {
  const status = streakStatus(streak, now);
  return status === 'safe' || status === 'at-risk' || status === 'rescuable';
}

/* -------------------------------------------------------------------------- */
/* Achievements                                                               */
/* -------------------------------------------------------------------------- */

export type Achievement = {
  id: string;
  title: string;
  description: string;
  /** Emoji used as the badge face. Kept in data so the UI stays generic. */
  icon: string;
  /** Awarded when this returns true. Must be pure and cheap. */
  earned(profile: LearnerProfile, now: number): boolean;
};

const masteredCount = (profile: LearnerProfile, now: number): number =>
  Object.values(profile.concepts).filter((state: ConceptState) => computeMastery(state, now).level === 'mastered')
    .length;

/**
 * Distinct local days with at least one graded attempt in the last `days` days.
 *
 * Read from the attempt log rather than from the streak, because "five days
 * this week" is a different question from "five days running" — a student who
 * practises Monday to Friday and rests at the weekend has a perfect week and a
 * streak of five, and one who practises every other day has neither.
 */
export function activeDaysInWindow(profile: LearnerProfile, now: number, days = 7): string[] {
  const cutoff = now - (days - 1) * 86_400_000;
  const seen = new Set<string>();
  for (const attempt of profile.attempts) {
    if (attempt.status === 'malformed' || attempt.at < cutoff) continue;
    seen.add(dayKey(attempt.at));
  }
  return [...seen].sort();
}

/** The most graded attempts made on any one day still in the log. */
function busiestDay(profile: LearnerProfile): number {
  const perDay = new Map<string, number>();
  for (const attempt of profile.attempts) {
    if (attempt.status === 'malformed') continue;
    const key = dayKey(attempt.at);
    perDay.set(key, (perDay.get(key) ?? 0) + 1);
  }
  let most = 0;
  for (const count of perDay.values()) most = Math.max(most, count);
  return most;
}

const practisedConcepts = (profile: LearnerProfile): number =>
  Object.values(profile.concepts).filter((state) => state.attempts > 0).length;

const totalReviews = (profile: LearnerProfile): number =>
  Object.values(profile.concepts).reduce((sum, state) => sum + state.reviews, 0);

/**
 * How many quests a day carries.
 *
 * Declared here rather than in `quests.ts` because the `perfect-day` badge has
 * to name it, and quests already depend on this module.
 */
export const DAILY_QUEST_COUNT = 3;

export const ACHIEVEMENTS: Achievement[] = [
  {
    id: 'first-steps',
    title: 'First Steps',
    description: 'Answer your first question.',
    icon: '🌱',
    earned: (profile) => profile.stats.totalAttempts >= 1,
  },
  {
    /*
     * The key to building on the map (`build.ts` in `@yuan-math/world`).
     *
     * Ten *answers*, not ten correct ones. Gating a toy on accuracy would take
     * it away from exactly the student who most needs a reason to keep
     * answering, and a wrong answer is still a question attempted. Malformed
     * input is not an attempt at all (`GradeResult.status`), so this cannot be
     * farmed by submitting an empty box ten times.
     */
    id: 'ten-problems',
    title: 'Groundbreaker',
    description: 'Answer ten questions.',
    icon: '🧱',
    earned: (profile) => profile.stats.totalAttempts >= 10,
  },
  {
    id: 'first-lesson',
    title: 'Lesson One',
    description: 'Finish your first lesson.',
    icon: '📘',
    earned: (profile) => profile.stats.lessonsCompleted >= 1,
  },
  {
    id: 'first-mastery',
    title: 'Mastered It',
    description: 'Reach mastery on your first concept.',
    icon: '🔑',
    earned: (profile, now) => masteredCount(profile, now) >= 1,
  },
  {
    id: 'five-mastered',
    title: 'Building Foundations',
    description: 'Master five concepts.',
    icon: '🧱',
    earned: (profile, now) => masteredCount(profile, now) >= 5,
  },
  {
    id: 'ten-mastered',
    title: 'Solid Ground',
    description: 'Master ten concepts.',
    icon: '⛰️',
    earned: (profile, now) => masteredCount(profile, now) >= 10,
  },
  {
    id: 'streak-3',
    title: 'Three in a Row',
    description: 'Practise three days running.',
    icon: '🔥',
    earned: (profile) => profile.streak.current >= 3,
  },
  {
    id: 'streak-7',
    title: 'A Full Week',
    description: 'Practise seven days running.',
    icon: '🗓️',
    earned: (profile) => profile.streak.current >= 7,
  },
  {
    id: 'streak-30',
    title: 'A Month of Mathematics',
    description: 'Practise thirty days running.',
    icon: '🏔️',
    earned: (profile) => profile.streak.longest >= 30,
  },
  {
    id: 'hundred-problems',
    title: 'One Hundred Problems',
    description: 'Answer one hundred questions.',
    icon: '💯',
    earned: (profile) => profile.stats.totalAttempts >= 100,
  },
  {
    id: 'precision',
    title: 'Precision',
    description: 'Answer twenty questions with 90% accuracy or better.',
    icon: '🎯',
    earned: (profile) =>
      profile.stats.totalAttempts >= 20 && profile.stats.totalCorrect / profile.stats.totalAttempts >= 0.9,
  },
  {
    id: 'comeback',
    title: 'Comeback',
    description: 'Master a concept you once got wrong three times.',
    icon: '🌗',
    earned: (profile, now) =>
      Object.values(profile.concepts).some(
        (state) =>
          state.attempts - state.correct >= 3 && computeMastery(state, now).level === 'mastered',
      ),
  },
  {
    id: 'challenger',
    title: 'Challenger',
    description: 'Answer a difficulty-5 question correctly.',
    icon: '⚡',
    earned: (profile) => Object.values(profile.concepts).some((state) => state.ceiling >= 5),
  },
  {
    id: 'twenty-mastered',
    title: 'Bedrock',
    description: 'Master twenty concepts.',
    icon: '🏛️',
    earned: (profile, now) => masteredCount(profile, now) >= 20,
  },
  {
    id: 'five-hundred-problems',
    title: 'Five Hundred',
    description: 'Answer five hundred questions.',
    icon: '🧮',
    earned: (profile) => profile.stats.totalAttempts >= 500,
  },
  {
    id: 'combo-10',
    title: 'On a Roll',
    description: 'Answer ten questions correctly in a row.',
    icon: '🎳',
    earned: (profile) => (profile.stats.bestCombo ?? 0) >= 10,
  },
  {
    id: 'combo-20',
    title: 'Unbroken',
    description: 'Answer twenty questions correctly in a row.',
    icon: '🌠',
    earned: (profile) => (profile.stats.bestCombo ?? 0) >= 20,
  },
  {
    id: 'first-quest',
    title: 'Quest Begun',
    description: 'Finish your first daily quest.',
    icon: '🧭',
    earned: (profile) => (profile.stats.questsCompleted ?? 0) >= 1,
  },
  {
    id: 'quest-25',
    title: 'Cartographer',
    description: 'Finish twenty-five daily quests.',
    icon: '🗺️',
    earned: (profile) => (profile.stats.questsCompleted ?? 0) >= 25,
  },
  {
    id: 'perfect-day',
    title: 'A Clean Sweep',
    description: 'Finish every quest in one day.',
    icon: '🌟',
    earned: (profile) => (profile.daily?.claimedQuestIds.length ?? 0) >= DAILY_QUEST_COUNT,
  },
  {
    id: 'full-week',
    title: 'Five Days in Seven',
    description: 'Practise on five days within one week.',
    icon: '📅',
    earned: (profile, now) => activeDaysInWindow(profile, now, 7).length >= 5,
  },
  {
    id: 'wide-reader',
    title: 'Wide Reader',
    description: 'Practise ten different concepts.',
    icon: '🧩',
    earned: (profile) => practisedConcepts(profile) >= 10,
  },
  {
    id: 'review-25',
    title: 'Nothing Forgotten',
    description: 'Complete twenty-five spaced reviews.',
    icon: '🔁',
    earned: (profile) => totalReviews(profile) >= 25,
  },
  {
    id: 'deep-diver',
    title: 'Deep Diver',
    description: 'Answer a difficulty-4 question in five different concepts.',
    icon: '🧗',
    earned: (profile) => Object.values(profile.concepts).filter((state) => state.ceiling >= 4).length >= 5,
  },
  {
    id: 'marathon',
    title: 'Marathon',
    description: 'Answer thirty questions in a single day.',
    icon: '🏃',
    earned: (profile) => busiestDay(profile) >= 30,
  },
];


const ACHIEVEMENTS_BY_ID = new Map(ACHIEVEMENTS.map((a) => [a.id, a]));

export function achievement(id: string): Achievement | undefined {
  return ACHIEVEMENTS_BY_ID.get(id);
}

/** Achievements newly earned by this profile. Never revokes an earned badge. */
export function newlyEarned(profile: LearnerProfile, now: number): Achievement[] {
  const owned = new Set(profile.achievements.map((entry) => entry.id));
  return ACHIEVEMENTS.filter((item) => !owned.has(item.id) && item.earned(profile, now));
}
